import React, {useState, useEffect, useRef} from 'react';
import { useTranslation } from 'react-i18next';
import './BrokenRoundsPageStyles.scss';
import { ResolveAllSessionsModal } from './ResolveAllSessionsModal';
import { RetryAllModal } from './RetryAllModal';
import { OnHoldAllModal } from './OnHoldAllModal';
import { Empty, Modal, Pagination, DatePicker, Button, Space, Tooltip } from 'antd';
import { ContentComponent } from '../../Components/ContentComponent/ContentComponent';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
    blockSession,
    getBrokenRounds,
    getBrokenRoundsCount,
    getBrokenRoundsJobStatus,
    resolveSession,
    retrySession,
    retrySessionBatch,
    resolveSessionBatch,
    onHoldAllRoundsBatch,
    resetBrokenRoundsJobData,
} from '../../redux/actions/configProvider/broken-rounds-actions';
import {
    getBrokenRoundsData,
    getBrokenRoundsMetadata,
    getJobStatusData,
    getJobUntilTimeData,
    getRoundUuidsData,
    getSessionIdsData,
    getTotalNumberOfRoundsData,
} from '../../redux/selectors/backoffice/broken-rounds-selector';
import SimplifiedCustomTable from '../../Components/Table/SimplifiedCustomTable';
import { brokenRoundsAllColumns } from '../Reports/columnsData';
import { Overlay } from '../../Components/Overlay/Overlay';
import { IBrokenRoundData } from '../../helpers/interfaces';
import { HeaderTitleComponent } from '../../Components/ContentComponent/HeaderTitleComponent';
import {
    LoadingOutlined,
    SyncOutlined,
    CloseCircleOutlined,
    SettingOutlined,
} from '@ant-design/icons/lib';
import { Select } from 'antd';
import StatusHistory from './StatusHistory';
import { SERVER_URL, DISABLE_MANUAL_RESOLVE_GAME_TYPES } from '../../utils/config';
import { getAuthenticatedFetchOptions, getLoginRequestHeader, Messages } from '../../utils';
import Search from 'antd/es/input/Search';
import i18next from 'i18next';
import { ITableData } from '../Reports/columnsData';
import { TableTools } from './TableTools';
import dayjs from "dayjs";
import {Loader} from "../../Components/Loader/Loader";

const { Option } = Select;
const { RangePicker } = DatePicker;

interface IBrokenRoundsPagingInfo {
    pageNumber: number;
    pageSize: number;
    sortBy: string;
    order: string;
}

interface IBrokenRoundsMetadata {
    pagingInfo: IBrokenRoundsPagingInfo;
    totalNumberOfElements: number;
}

interface IProps {
    data: IBrokenRoundData[];
    metadata: IBrokenRoundsMetadata;
    getBrokenRounds: Function;
    getBrokenRoundsCount: Function;
    resolveSession: Function;
    retrySession: Function;
    blockSession: Function;
    retrySessionBatch: Function;
    resolveSessionBatch: Function;
    onHoldRoundsBatch: Function;
    getBrokenRoundsJobStatus: Function;
    resetBrokenRoundsJobData: Function;
    totalNumberOfElements: number | null;
    sessionIds: number[] | null;
    roundUuids: string[] | null;
    isJobRunning: boolean;
}

interface IBrokenRoundRequestBody {
    externalPlayerId: string | null;
    playerSession: string | null;
    roundUuids: string | null;
    playerId: number | null;
    sessionId: number | null;
    currencyCode: string | null;
    roundStatuses: string[] | null;
    transactionUuid: string | null;
    roundStartedFrom: string | null;
    roundStartedTo: string | null;
    roundFailedFrom: string | null;
    roundFailedTo: string | null;
    brandNames: string[] | null;
    operatorNames: string | null;
    pagingInfo: IBrokenRoundsPagingInfo;
}

const DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE = 100;
const PAGE_SIZE_ARRAY = ['10', '20', '50', '100', '1000'];
const MAX_SEARCH_LENGTH = 1000;

const initialBodyRequest: IBrokenRoundRequestBody = {
    externalPlayerId: null,
    pagingInfo: {
        pageNumber: 1,
        pageSize: DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE,
        sortBy: 'id',
        order: 'DESCENDING',
    },
    playerId: null,
    playerSession: null,
    roundUuids: null,
    sessionId: null,
    roundStatuses: null,
    currencyCode: null,
    transactionUuid: null,
    roundStartedFrom: null,
    roundStartedTo: null,
    roundFailedFrom: null,
    roundFailedTo: null,
    brandNames: null,
    operatorNames: null,
};

const initialRoundInfo: IBrokenRoundData = {
    brandId: 0,
    brandName: '',
    failReason: '',
    failedRoundUuid: '',
    gameId: 0,
    gameName: '',
    gameType: '',
    id: 0,
    operatorId: 0,
    playerId: 0,
    playerSession: '',
    roundStatus: '',
    walletErrorCode: 0,
    walletErrorDescription: '',
    walletErrorDetails: '',
    roundUuid: '',
    sessionId: 0,
    startId: 0,
    transactionType: '',
    externalPlayerId: '',
    amount: 0,
    currencyCode: '',
    created: '',
    transactionUuid: '',
    roundStartedAt: '',
    roundFailedAt: '',
};

const brokenRoundsColumnsInitialState: ITableData[] = [
    brokenRoundsAllColumns.info,
    brokenRoundsAllColumns.roundStatus,
    brokenRoundsAllColumns.roundStartedAt,
    brokenRoundsAllColumns.roundFailedAt,
    brokenRoundsAllColumns.amount,
    brokenRoundsAllColumns.brandName,
    brokenRoundsAllColumns.operatorName,
    brokenRoundsAllColumns.roundUuid,
    brokenRoundsAllColumns.transactionUuid,
    brokenRoundsAllColumns.sessionId,
    brokenRoundsAllColumns.externalPlayerId,
];

const BrokenRoundsPage = (props: IProps) => {
    const { t } = useTranslation();
    const {
        data,
        metadata,
        getBrokenRounds,
        getBrokenRoundsCount,
        resolveSession,
        retrySession,
        blockSession,
        totalNumberOfElements,
        retrySessionBatch,
        onHoldRoundsBatch,
        sessionIds,
        roundUuids,
        resolveSessionBatch,
        getBrokenRoundsJobStatus,
        resetBrokenRoundsJobData,
        isJobRunning,
    } = props;
    const [brokenRoundsColumns, setBrokenRoundsColumns] = useState(brokenRoundsColumnsInitialState);
    const defaultRoundFailedFrom = dayjs()
        .subtract(7, 'days')
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss');
    const defaultRoundFailedTo = dayjs().endOf('day').format('YYYY-MM-DDTHH:mm:ss');
    const [requestBody, setRequestBody] = useState({
        ...initialBodyRequest,
        roundStatuses: ['ON_RETRY', 'BLOCKED'],
        roundFailedFrom: defaultRoundFailedFrom,
        roundFailedTo: defaultRoundFailedTo,
    });
    const [roundInfo, setRoundInfo] = useState({ ...initialRoundInfo });
    const [roundInfoPopupVisible, changeInfoPopupVisibility] = useState(false);
    const [loadingResolve, setLoadingResolveStatus] = useState(false);
    const [loadingRetry, setLoadingRetryStatus] = useState(false);
    const [searchKey, setSearchKey] = useState('roundUuids');
    const [searchValue, setSearchValue] = useState('');
    const [resolveAllModalVisible, setResolveAllModalVisible] = useState(false);
    const [retryAllModalVisible, setRetryAllModalVisible] = useState(false);
    const [onHoldAllModalVisible, setOnHoldAllModalVisible] = useState(false);
    const [tableToolsModalVisible, setTableToolsModalVisible] = useState(false);
    const isGameTypeResolveBlocked = (gameType: string): boolean =>
        DISABLE_MANUAL_RESOLVE_GAME_TYPES.includes(gameType);
    const isRetryAllDisabled = () => {
        const validStatuses = ['BLOCKED', 'ON_HOLD'];
        const hasBlockedGameType =
            DISABLE_MANUAL_RESOLVE_GAME_TYPES.length > 0 &&
            (data || []).some((round: IBrokenRoundData) => isGameTypeResolveBlocked(round.gameType));
        return (
            hasBlockedGameType ||
            isJobRunning ||
            requestBody.roundStatuses.length !== 1 ||
            !requestBody.roundStatuses.every((status) => validStatuses.includes(status))
        );
    };
    const isResolveDisabled = () => {
        const validStatuses = ['BLOCKED', 'ON_HOLD', 'ON_RETRY'];
        const hasBlockedGameType =
            DISABLE_MANUAL_RESOLVE_GAME_TYPES.length > 0 &&
            (data || []).some((round: IBrokenRoundData) => isGameTypeResolveBlocked(round.gameType));
        return (
            hasBlockedGameType ||
            isJobRunning ||
            requestBody.roundStatuses.length !== 1 ||
            !requestBody.roundStatuses.every((status) => validStatuses.includes(status))
        );
    };
    const isOnHoldAllDisabled = () => {
        const validStatuses = ['ON_RETRY'];
        const hasBlockedGameType =
            DISABLE_MANUAL_RESOLVE_GAME_TYPES.length > 0 &&
            (data || []).some((round: IBrokenRoundData) => isGameTypeResolveBlocked(round.gameType));
        return (
            hasBlockedGameType ||
            isJobRunning ||
            requestBody.roundStatuses.length !== 1 ||
            !requestBody.roundStatuses.every((status) => validStatuses.includes(status))
        );
    };

    const updateRequestBody = (newData: Partial<typeof requestBody>) => {
        setRequestBody((prevBody) => ({
            ...prevBody,
            ...newData,
            pagingInfo: {
                ...prevBody.pagingInfo,
                ...newData.pagingInfo,
            },
        }));
    };

    const showInfo = (roundData: IBrokenRoundData) => {
        setRoundInfo(roundData);
        changeInfoPopupVisibility(true);
    };

    const getSupplementedData = () => {
        data.forEach((round: any) => {
            round.callback = showInfo;
        });

        return data;
    };

    const getInfo = () => {
        const result = [];

        for (const key in roundInfo) {
            if (key !== 'callback') {
                result.push(
                    <div key={key} className={'broken-round-info__item'}>
                        <p className={'broken-round-info__title'}>
                            {key === 'id' ? t('broken_round_id') : t(key)}
                        </p>
                        <p className={'broken-round-info__value'}>
                            {roundInfo[key as keyof IBrokenRoundData]}
                        </p>
                    </div>,
                );
            }
        }

        result.push(<StatusHistory key={roundInfo.roundUuid} roundUuid={roundInfo.roundUuid} />);

        return result;
    };

    const onResolve = () => {
        setLoadingResolveStatus(true);
        resolveSession(roundInfo.sessionId);
        setTimeout(() => {
            setLoadingResolveStatus(false);
            changeInfoPopupVisibility(false);
            getBrokenRounds(requestBody);
        }, 1000);
    };

    const onPageChange = (pageNumber: number) => {
        const pagingInfo = { ...requestBody.pagingInfo };

        pagingInfo.pageNumber = pageNumber;
        setRequestBody({ ...requestBody, pagingInfo });

        getBrokenRounds({ ...requestBody, pagingInfo });
    };

    const onShowSizeChange = (pageNumber: number, size: number) => {
        const pagingInfo = requestBody.pagingInfo;

        pagingInfo.pageNumber = pageNumber;
        pagingInfo.pageSize = size;

        setRequestBody({ ...requestBody, pagingInfo });
        getBrokenRounds(requestBody);
    };

    const onSearch = (value: string) => {
        const regularExp = new RegExp('^[0-9]*$');

        if (searchKey === 'sessionId' && !regularExp.test(value)) {
            setSearchValue(t('input_a_number'));
            return;
        }

        const body: any = {
            ...requestBody,
            pagingInfo: initialBodyRequest.pagingInfo,
            playerSession: null,
            roundUuids: null,
            sessionId: null,
            externalPlayerId: null,
            brandNames: null,
            transactionUuid: null,
            operatorNames: null
        };
        if (searchValue.length > 0) {
            if (searchKey === 'roundUuids') {
                body[searchKey] = value.split(' ').map((s) => s.trim());
            } else if (searchKey === 'brandNames' || searchKey === 'operatorNames') {
                body[searchKey] = [value.trim()]
            } else {
                body[searchKey] = value;
            }
            if (searchValue.length > MAX_SEARCH_LENGTH) {
                Messages.error(i18next.t('search_value_is_too_long', {max: MAX_SEARCH_LENGTH}));
                return;
            }
        }
        setRequestBody(body);

        getBrokenRounds(body);

    };

    const onDateChange = (dates: any) => {
        const formatString = 'YYYY-MM-DDTHH:mm:ss';
        const roundFailedFrom = dates ? dates[0].startOf('day').format(formatString) : null;
        const roundFailedTo = dates ? dates[1].endOf('day').format(formatString) : null;

        setRequestBody({
            ...requestBody,
            roundFailedFrom,
            roundFailedTo,
            pagingInfo: {
                ...requestBody.pagingInfo,
                pageNumber: 1,
            },
        });
    };

    const onStatusFilterChange = (value: string) => {
        const status: [] | string[] = value === 'all' ? [] : [value];

        updateRequestBody({
            roundStatuses: value === 'ON_RETRY_BLOCKED' ? ['ON_RETRY', 'BLOCKED'] : status,
            pagingInfo: {
                ...requestBody.pagingInfo,
                pageNumber: 1,
            },
        });
    };

    const refreshAfterRetry = () => {
        setTimeout(() => {
            setLoadingRetryStatus(false);
            changeInfoPopupVisibility(false);
            getBrokenRounds(requestBody);
        }, 1000);
    };
    const onSearchKeyChange = (key: string) => {
        setSearchKey(key);
    };

    const searchFieldSuffix = () => (
        <CloseCircleOutlined
            className={`broken-rounds-search__clear${searchValue.length > 0 ? '__visible' : ''}`}
            onClick={() => {
                if (searchValue.length > 0) {
                    refreshReportAfterSearch();
                }
            }}
        />
    );

    const refreshReportAfterSearch = () => {
        const { roundFailedTo, roundFailedFrom, roundStatuses } = requestBody;
        setSearchValue('');
        setRequestBody({
            ...initialBodyRequest,
            roundFailedFrom,
            roundFailedTo,
            roundStatuses,
        });
        getBrokenRounds({
            ...initialBodyRequest,
            roundFailedFrom,
            roundFailedTo,
            roundStatuses,
        });
    };

    const searchOnChange = (e: any) => {
        setSearchValue(e.target.value);
    };

    const onRetry = () => {
        setLoadingRetryStatus(true);
        retrySession(roundInfo.roundUuid);
        refreshAfterRetry();
    };

    const onBlock = () => {
        setLoadingRetryStatus(true);
        blockSession(roundInfo.roundUuid);
        refreshAfterRetry();
    };

    const reloadData = () => {
        getBrokenRounds(requestBody);
    };

    const handleResolveAll = () => {
        getBrokenRoundsCount(requestBody);
        setResolveAllModalVisible(true);
    };

    const handleRetryAll = () => {
        getBrokenRoundsCount(requestBody);
        setRetryAllModalVisible(true);
    };

    const handleOnHoldAll = () => {
        getBrokenRoundsCount(requestBody);
        setOnHoldAllModalVisible(true);
    };

    const handleExport = async () => {
        const body = {
            ...requestBody,
            exportFormat: 'CSV',
            totalNumberOfElements: metadata.totalNumberOfElements.toString(),
        };

        try {
            const response = await fetch(`${SERVER_URL}/rounds/broken-rounds/export`, getAuthenticatedFetchOptions({
                method: 'POST',
                headers: {
                    ...getLoginRequestHeader('application/json'),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            }));

            if (!response.ok) {
                let errorMessage = 'Failed to export data';

                try {
                    const errorData = await response.json();
                    if (errorData.error.message) {
                        errorMessage = errorData.error.message;
                    } else {
                        errorMessage = JSON.stringify(errorData);
                    }
                } catch (jsonError) {
                    errorMessage = await response.text();
                }

                throw new Error(errorMessage);
            }

            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'export.csv';
            if (contentDisposition && contentDisposition.includes('filename=')) {
                filename = contentDisposition.split('filename=')[1].trim();
                filename = filename.replace(/^"|"$/g, '');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            Messages.error('Error exporting data: ' + error.message);
        }
    };

    const requestBodyRef = useRef(requestBody);
    useEffect(() => {
        requestBodyRef.current = requestBody;
    }, [requestBody]);

    useEffect(() => {
        getBrokenRounds(requestBodyRef.current);
    }, [getBrokenRounds]);

    useEffect(() => {
        const pollingInterval = isJobRunning ? 3000 : 30000;

        const intervalId = setInterval(() => {
            getBrokenRoundsJobStatus();
        }, pollingInterval);

        const timeoutId = setTimeout(
            () => {
                clearInterval(intervalId);
            },
            30 * 60 * 1000,
        );

        /**
         * Cleanup: Runs on unmount or whenever getBrokenRoundsJobStatus/isJobRunning changes.
         * - Clear intervalId: Stops polling (safe if 30-min timeout already cleared it).
         * - Clear timeoutId: Prevents 30-min timeout from firing on unmounted component.
         */
        return () => {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, [getBrokenRoundsJobStatus, isJobRunning]);

    useEffect(() => {
        getBrokenRoundsJobStatus();

        return () => {
            resetBrokenRoundsJobData();
        };
    }, [getBrokenRoundsJobStatus, resetBrokenRoundsJobData]);

    return (
        <ContentComponent
            header={
                <>
                    <HeaderTitleComponent
                        title={t('broken_rounds')}
                        customBreadcrumbs={<div>{t('all')}</div>}
                    />
                    <Space.Compact className="broken-rounds-search__wrapper mt-2">
                        <Button
                            icon={<SettingOutlined />}
                            onClick={() => {
                                setTableToolsModalVisible(true);
                            }}
                            className="broken-rounds-search__refresh"
                        />
                        <Button
                            icon={<SyncOutlined />}
                            onClick={reloadData}
                            className="broken-rounds-search__refresh"
                        />
                        <RangePicker
                            className="broken-rounds-search__datepicker"
                            onChange={(e: any) => onDateChange(e)}
                            defaultValue={[dayjs().subtract(7, 'days'), dayjs()]}
                        />
                        <Select
                            style={{ width: '160px' }}
                            className="broken-rounds-search__status"
                            defaultValue="ON_RETRY_BLOCKED"
                            onChange={onStatusFilterChange}
                            popupMatchSelectWidth={true}
                        >
                            <Option value="all">{t('all')}</Option>
                            <Option value="ON_RETRY_BLOCKED">{t('on_retry&blocked')}</Option>
                            <Option value="ON_RETRY">{t('on_retry')}</Option>
                            <Option value="BLOCKED">{t('blocked')}</Option>
                            <Option value="ON_HOLD">{t('on_hold')}</Option>
                            <Option value="RESOLVED">{t('resolved')}</Option>
                            <Option value="MANUALLY_RESOLVED">{t('manually_resolved')}</Option>
                            <Option value="NOT_BLOCKED">{t('not_blocked')}</Option>
                        </Select>
                        <Button className="broken-rounds-control-button" onClick={handleExport}>
                            Export to CSV
                        </Button>
                        <Tooltip
                            title={
                                (data || []).some((round: IBrokenRoundData) =>
                                    isGameTypeResolveBlocked(round.gameType),
                                )
                                    ? `Resolving is disabled by DISABLE_MANUAL_RESOLVE_GAME_TYPES for game types: ${DISABLE_MANUAL_RESOLVE_GAME_TYPES.join(', ')}`
                                    : ''
                            }
                        >
                            <Button
                                className="broken-rounds-control-button"
                                disabled={isResolveDisabled()}
                                onClick={handleResolveAll}
                            >
                                Resolve all
                            </Button>
                        </Tooltip>
                        <Tooltip
                            title={
                                (data || []).some((round: IBrokenRoundData) =>
                                    isGameTypeResolveBlocked(round.gameType),
                                )
                                    ? `Retrying is disabled by DISABLE_MANUAL_RESOLVE_GAME_TYPES for game types: ${DISABLE_MANUAL_RESOLVE_GAME_TYPES.join(', ')}`
                                    : ''
                            }
                        >
                            <Button
                                className="broken-rounds-control-button"
                                disabled={isRetryAllDisabled()}
                                onClick={handleRetryAll}
                            >
                                Retry all
                            </Button>
                        </Tooltip>
                        <Tooltip
                            title={
                                (data || []).some((round: IBrokenRoundData) =>
                                    isGameTypeResolveBlocked(round.gameType),
                                )
                                    ? `Putting on hold is disabled by DISABLE_MANUAL_RESOLVE_GAME_TYPES for game types: ${DISABLE_MANUAL_RESOLVE_GAME_TYPES.join(', ')}`
                                    : ''
                            }
                        >
                            <Button
                                className="broken-rounds-control-button"
                                disabled={isOnHoldAllDisabled()}
                                onClick={handleOnHoldAll}
                            >
                                Put on hold all
                            </Button>
                        </Tooltip>
                        {isJobRunning && (
                            <p
                                style={{
                                    display: 'flex',
                                    margin: '0',
                                    marginLeft: '8px',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <LoadingOutlined />{' '}
                                <span style={{ marginLeft: '8px' }}>Job is running</span>
                            </p>
                        )}
                    </Space.Compact>
                    <ResolveAllSessionsModal
                        sessionIds={sessionIds}
                        resolveAllModalVisible={resolveAllModalVisible}
                        resolveSessionBatch={resolveSessionBatch}
                        setResolveAllModalVisible={setResolveAllModalVisible}
                        totalNumberOfElements={totalNumberOfElements}
                    />
                    <RetryAllModal
                        roundUuids={roundUuids}
                        retryAllModalVisible={retryAllModalVisible}
                        retrySessionBatch={retrySessionBatch}
                        setRetryAllModalVisible={setRetryAllModalVisible}
                        totalNumberOfElements={totalNumberOfElements}
                    />
                    <OnHoldAllModal
                        roundUuids={roundUuids}
                        onHoldAllModalVisible={onHoldAllModalVisible}
                        onHoldRoundsBatch={onHoldRoundsBatch}
                        setOnHoldAllModalVisible={setOnHoldAllModalVisible}
                        totalNumberOfElements={totalNumberOfElements}
                    />
                    <TableTools
                        tableToolsModalVisible={tableToolsModalVisible}
                        setTableToolsModalVisible={setTableToolsModalVisible}
                        brokenRoundsColumns={brokenRoundsColumns}
                        setBrokenRoundsColumns={setBrokenRoundsColumns}
                    />
                    <Space.Compact style={{ marginBottom: '8px', display: 'flex', width: '100%' }}>
                        <Space.Compact className="broken-rounds-search__wrapper">
                            <Select
                                style={{ width: '160px' }}
                                defaultValue={t('roundUuid')}
                                className="broken-rounds-search__select"
                                onChange={onSearchKeyChange}
                                popupMatchSelectWidth={true}
                            >
                                <Option value="roundUuids">{t('roundUuid')}</Option>
                                <Option value="externalPlayerId">{t('externalPlayerId')}</Option>
                                <Option value="playerSession">{t('playerSession')}</Option>
                                <Option value="sessionId">{t('sessionId')}</Option>
                                <Option value="transactionUuid">{t('transactionUuid')}</Option>
                                <Option value="brandNames">{t('brandName')}</Option>
                                <Option value="operatorNames">{t('operatorName')}</Option>
                            </Select>
                            <Search
                                value={searchValue}
                                onChange={searchOnChange}
                                suffix={searchFieldSuffix()}
                                className="broken-rounds-search__search-field"
                                placeholder={
                                    searchKey === 'sessionId'
                                        ? t('input_a_number')
                                        : t('enter_search_value')
                                }
                                onSearch={onSearch}
                                enterButton
                                style={{ flex: 1 }}
                            />
                        </Space.Compact>
                        <Space.Compact className="broken-rounds-search__pagination">
                            {data ? (
                                <Pagination
                                    className="table-pagination"
                                    onChange={onPageChange}
                                    total={metadata.totalNumberOfElements}
                                    current={metadata.pagingInfo.pageNumber}
                                    pageSize={metadata.pagingInfo.pageSize}
                                    pageSizeOptions={PAGE_SIZE_ARRAY}
                                    showSizeChanger
                                    onShowSizeChange={onShowSizeChange}
                                />
                            ) : null}
                        </Space.Compact>
                    </Space.Compact>
                </>
            }
            innerContent={
                data ? (
                    data.length ? (
                        <>
                            <SimplifiedCustomTable
                                dataSource={getSupplementedData()}
                                columns={brokenRoundsColumns}
                            />
                            <Modal
                                className="broken-round-info__modal"
                                title={t('info')}
                                open={roundInfoPopupVisible}
                                onOk={() => changeInfoPopupVisibility(false)}
                                onCancel={() => changeInfoPopupVisibility(false)}
                                closable={false}
                                footer={[
                                    (roundInfo.roundStatus.includes('BLOCKED') ||
                                        roundInfo.roundStatus.includes('ON_HOLD')) && (
                                        <Tooltip
                                            key={'retry'}
                                            title={
                                                isGameTypeResolveBlocked(roundInfo.gameType)
                                                    ? `Retrying is disabled by DISABLE_MANUAL_RESOLVE_GAME_TYPES for game types: ${DISABLE_MANUAL_RESOLVE_GAME_TYPES.join(', ')}`
                                                    : ''
                                            }
                                        >
                                            <Button
                                                shape="round"
                                                onClick={onRetry}
                                                disabled={isGameTypeResolveBlocked(
                                                    roundInfo.gameType,
                                                )}
                                            >
                                                <>
                                                    {loadingRetry ? (
                                                        <LoadingOutlined
                                                            style={{
                                                                marginRight: '8px',
                                                            }}
                                                        />
                                                    ) : null}
                                                    {t('retry')}
                                                </>
                                            </Button>
                                        </Tooltip>
                                    ),
                                    roundInfo.roundStatus.includes('ON_RETRY') && (
                                        <Tooltip
                                            key={'block'}
                                            title={
                                                isGameTypeResolveBlocked(roundInfo.gameType)
                                                    ? `Putting on hold is disabled by DISABLE_MANUAL_RESOLVE_GAME_TYPES for game types: ${DISABLE_MANUAL_RESOLVE_GAME_TYPES.join(', ')}`
                                                    : ''
                                            }
                                        >
                                            <Button
                                                shape="round"
                                                onClick={onBlock}
                                                disabled={isGameTypeResolveBlocked(
                                                    roundInfo.gameType,
                                                )}
                                            >
                                                <>
                                                    {loadingRetry ? (
                                                        <LoadingOutlined
                                                            style={{
                                                                marginRight: '8px',
                                                            }}
                                                        />
                                                    ) : null}
                                                    Put on hold
                                                </>
                                            </Button>
                                        </Tooltip>
                                    ),
                                    <Tooltip
                                        key={'resolve_session'}
                                        title={
                                            isGameTypeResolveBlocked(roundInfo.gameType)
                                                ? `Resolving is disabled by DISABLE_MANUAL_RESOLVE_GAME_TYPES for game types: ${DISABLE_MANUAL_RESOLVE_GAME_TYPES.join(', ')}`
                                                : ''
                                        }
                                    >
                                        <Button
                                            shape="round"
                                            onClick={onResolve}
                                            disabled={
                                                roundInfo.roundStatus.includes('RESOLVED') ||
                                                roundInfo.roundStatus.includes('TRX_NOT_FOUND') ||
                                                isGameTypeResolveBlocked(roundInfo.gameType)
                                            }
                                        >
                                            <>
                                                {loadingResolve ? (
                                                    <LoadingOutlined
                                                        style={{
                                                            marginRight: '8px',
                                                        }}
                                                    />
                                                ) : null}
                                                Resolve Manually
                                            </>
                                        </Button>
                                    </Tooltip>,
                                    <Button
                                        shape="round"
                                        type="primary"
                                        key={'ok'}
                                        onClick={() => changeInfoPopupVisibility(false)}
                                    >
                                        {t('ok')}
                                    </Button>,
                                ]}
                            >
                                <div className="broken-round-info">{getInfo()}</div>
                            </Modal>
                            <Overlay
                                isVisible={roundInfoPopupVisible}
                                switchState={changeInfoPopupVisibility}
                            />
                        </>
                    ) : (
                        <Empty description={t('no_data')} />
                    )
                ) : (
                    <Loader style={{ height: '90vh' }} />
                )
            }
        />
    );
};

const mapStateToProps = (state: any) => ({
    data: getBrokenRoundsData(state),
    metadata: getBrokenRoundsMetadata(state),
    totalNumberOfElements: getTotalNumberOfRoundsData(state),
    sessionIds: getSessionIdsData(state),
    roundUuids: getRoundUuidsData(state),
    isJobRunning: getJobStatusData(state),
    jobUntilTime: getJobUntilTimeData(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getBrokenRounds: (data: IBrokenRoundRequestBody) => dispatch(getBrokenRounds(data)),
    getBrokenRoundsCount: (data: any) => dispatch(getBrokenRoundsCount(data)),
    resolveSession: (data: number) => dispatch(resolveSession(data)),
    retrySession: (data: string) => dispatch(retrySession(data)),
    blockSession: (data: string) => dispatch(blockSession(data)),
    retrySessionBatch: (data: string) => dispatch(retrySessionBatch(data)),
    onHoldRoundsBatch: (data: string) => dispatch(onHoldAllRoundsBatch(data)),
    resolveSessionBatch: (data: string) => dispatch(resolveSessionBatch(data)),
    getBrokenRoundsJobStatus: () => dispatch(getBrokenRoundsJobStatus()),
    resetBrokenRoundsJobData: () => dispatch(resetBrokenRoundsJobData()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BrokenRoundsPage);
